import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Users, Package, FileText, CheckCircle, Trash2, Edit, ChevronDown, ChevronUp, Download, Check, Upload, Eye, BarChart3, TrendingUp, Loader2, Lock, Unlock } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import CierreTab from './CierreTab';
import BloqueadosTab from './BloqueadosTab';
import Modal from '../common/Modal';
import ProductCard from './ProductCard';
import CalendarioFases from './CalendarioFases';

const withAuthToken = (url) => {
    const token = localStorage.getItem('token');
    return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
};

// Formato venezolano: 60.325,60
const formatMoney = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0,00';
    return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// cerradaAt llega en hora Venezuela (sin zona): se muestra tal cual
const formatFechaHora = (iso) => iso
    ? new Date(iso).toLocaleString('es-VE', { dateStyle: 'medium', timeStyle: 'short' })
    : '';

const VentaEspecialAdmin = () => {
    const [ventas, setVentas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createModal, setCreateModal] = useState(false);
    const [selectedVenta, setSelectedVenta] = useState(null);
    const [view, setView] = useState('list'); // list, details, comparativa
    const [listaTab, setListaTab] = useState('publicadas'); // publicadas (incluye borradores), cerradas
    const [cerrarModal, setCerrarModal] = useState(false);
    const [cerrandoVenta, setCerrandoVenta] = useState(false);
    const [reabrirModal, setReabrirModal] = useState(false);
    const [reabriendoVenta, setReabriendoVenta] = useState(false);
    const [activeTab, setActiveTab] = useState('productos'); // productos, pedidos, calendario, archivos
    const [comparativa, setComparativa] = useState([]);
    const [comparativaLoading, setComparativaLoading] = useState(false);
    const [plantillaFile, setPlantillaFile] = useState(null);
    const [importandoPlantilla, setImportandoPlantilla] = useState(false);
    const [reemplazandoPlantilla, setReemplazandoPlantilla] = useState(false);
    const [exportandoPagos, setExportandoPagos] = useState(false);

    // Estado Pedidos
    const [pedidos, setPedidos] = useState([]);
    const [loadingPedidos, setLoadingPedidos] = useState(false);
    const [expandedPedido, setExpandedPedido] = useState(null);
    const [editandoPedidoId, setEditandoPedidoId] = useState(null);
    const [pedidoEditItems, setPedidoEditItems] = useState([]);
    const [savingPedido, setSavingPedido] = useState(false);

    // Estado Stock (solo lectura)
    const [stockData, setStockData] = useState([]);
    const [loadingStock, setLoadingStock] = useState(false);

    // Formulario venta
    const [formData, setFormData] = useState({
        titulo: '',
        descripcion: '',
        fechaInicio: '',
        fechaLimitePedido: '',
        fechaLimitePago: '',
        activa: false
    });

    // Productos importación
    const [importModal, setImportModal] = useState(false);
    const [manualModal, setManualModal] = useState(false);
    const [externalProducts, setExternalProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [importing, setImporting] = useState(false);
    const [apiError, setApiError] = useState(null);

    // Formulario producto manual
    const [productForm, setProductForm] = useState({
        codigo: '',
        descripcion: '',
        precio: '',
        unidad_manejo: 'UNIDAD',
        limite: ''
    });

    // Calendario fases (paint mode: { "YYYY-MM-DD": "phase_key", ... })
    const [diasPintados, setDiasPintados] = useState({});
    const [savingFases, setSavingFases] = useState(false);
    const [mesesCalendario, setMesesCalendario] = useState([]);

    // Info pago
    const [infoPagoForm, setInfoPagoForm] = useState({
        banco: '', cuenta: '', rif: '', titular: '',
        pagoMovil: { rif: '', banco: '', telefono: '' },
        emailsPedido: '', emailsPago: ''
    });
    const [savingInfo, setSavingInfo] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);

    useEffect(() => {
        fetchVentas();
    }, []);

    useEffect(() => {
        if (selectedVenta && activeTab === 'pedidos') {
            fetchPedidos(selectedVenta.id);
        }
        if (selectedVenta && activeTab === 'stock') {
            fetchStock(selectedVenta.id);
        }
    }, [selectedVenta, activeTab]);

    // Sync fases/info forms when selectedVenta changes or tab changes
    useEffect(() => {
        if (selectedVenta && activeTab === 'calendario') {
            const rawDias = selectedVenta.fasesCalendario || {};
            const existingDias = Array.isArray(rawDias) ? {} : rawDias;
            const dates = Object.keys(existingDias);
            let meses = [];

            if (dates.length > 0) {
                let minDate = null, maxDate = null;
                dates.forEach(d => {
                    const dt = new Date(d + 'T00:00:00');
                    if (!minDate || dt < minDate) minDate = dt;
                    if (!maxDate || dt > maxDate) maxDate = dt;
                });
                const startM = new Date(minDate.getFullYear(), minDate.getMonth() - 1, 1);
                const endM = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1);
                const cur = new Date(startM);
                while (cur <= endM) {
                    meses.push({ year: cur.getFullYear(), month: cur.getMonth() });
                    cur.setMonth(cur.getMonth() + 1);
                }
            } else {
                const now = new Date();
                meses = [
                    { year: now.getFullYear(), month: now.getMonth() },
                    { year: now.getFullYear() + (now.getMonth() + 1 > 11 ? 1 : 0), month: (now.getMonth() + 1) % 12 },
                    { year: now.getFullYear() + (now.getMonth() + 2 > 11 ? 1 : 0), month: (now.getMonth() + 2) % 12 },
                ];
            }

            const initDias = { ...existingDias };
            meses.forEach(m => {
                const daysInMonth = new Date(m.year, m.month + 1, 0).getDate();
                for (let d = 1; d <= daysInMonth; d++) {
                    const dt = new Date(m.year, m.month, d);
                    if (dt.getDay() === 0 || dt.getDay() === 6) {
                        const dateStr = `${m.year}-${String(m.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        if (!initDias[dateStr] && dates.length === 0) {
                            initDias[dateStr] = 'no_laborable';
                        }
                    }
                }
            });

            setDiasPintados(initDias);
            setMesesCalendario(meses);
        }
        if (selectedVenta && activeTab === 'archivos') {
            const info = selectedVenta.infoPago || {};
            setInfoPagoForm({
                banco: info.banco || 'Banco Venezolano de Crédito',
                cuenta: info.cuenta || '0104-0060-11-0600034901',
                rif: info.rif || 'J-30622535-8',
                titular: info.titular || 'Sura de Venezuela C.A.',
                pagoMovil: {
                    rif: info.pagoMovil?.rif || 'J-306225358',
                    banco: info.pagoMovil?.banco || 'Banco Venezolano de Crédito',
                    telefono: info.pagoMovil?.telefono || '0414-4423022'
                },
                emailsPedido: info.emailsPedido || 'mfernanda.guzman@sura.com.ve\nluis.herrera@suraven.com',
                emailsPago: info.emailsPago || 'tesoreriasdv01@sura.com.ve\nmfernanda.guzman@sura.com.ve\nluis.herrera@suraven.com'
            });
        }
    }, [selectedVenta?.id, activeTab]);

    const fetchVentas = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/ventas-especiales');
            setVentas(response.data);
        } catch (error) {
            console.error('Error fetching ventas:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStock = async (ventaId) => {
        try {
            setLoadingStock(true);
            const response = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/stock`);
            setStockData(response.data);
        } catch (error) {
            console.error('Error fetching stock:', error);
        } finally {
            setLoadingStock(false);
        }
    };

    const fetchPedidos = async (ventaId) => {
        try {
            setLoadingPedidos(true);
            const response = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/pedidos`);
            setPedidos(response.data);
        } catch (error) {
            console.error('Error fetching pedidos:', error);
        } finally {
            setLoadingPedidos(false);
        }
    };

    const handleCreateVenta = async (e) => {
        e.preventDefault();
        if (!plantillaFile) {
            alert('Debes subir la plantilla Excel llena para crear la venta.');
            return;
        }
        try {
            setImportandoPlantilla(true);
            const fd = new FormData();
            fd.append('titulo', formData.titulo);
            fd.append('descripcion', formData.descripcion || '');
            fd.append('archivo', plantillaFile);
            const res = await axiosInstance.post('/api/ventas-especiales/importar', fd);
            setCreateModal(false);
            setPlantillaFile(null);
            fetchVentas();
            alert(`Venta creada con ${res.data.productos} producto(s)` + (res.data.pesos ? ` y ${res.data.pesos} peso(s) de bolsa por kg` : '') + '.');
        } catch (error) {
            alert(error.response?.data?.error || 'Error creando venta');
        } finally {
            setImportandoPlantilla(false);
        }
    };

    const handleReplantilla = async (file) => {
        if (!file || !selectedVenta) return;
        try {
            setReemplazandoPlantilla(true);
            const fd = new FormData();
            fd.append('archivo', file);
            const res = await axiosInstance.post(`/api/ventas-especiales/${selectedVenta.id}/productos/plantilla`, fd);
            fetchProductos(selectedVenta.id);
            alert(`Productos actualizados: ${res.data.productos}` + (res.data.pesos ? ` (con ${res.data.pesos} peso(s) de bolsa por kg)` : '') + '.');
        } catch (error) {
            alert(error.response?.data?.error || 'Error al reemplazar la plantilla');
        } finally {
            setReemplazandoPlantilla(false);
        }
    };

    const handleOpenImport = async () => {
        setImportModal(true);
        setImporting(true);
        setApiError(null);
        try {
            const response = await axiosInstance.get('/api/ventas-especiales/productos-disponibles');
            setExternalProducts(response.data.precios || []);
        } catch (error) {
            console.error(error);
            setApiError('No se pudo conectar con el servidor de productos.');
            setExternalProducts([]);
        } finally {
            setImporting(false);
        }
    };

    const handleAddManualProduct = async (e) => {
        e.preventDefault();
        if (!selectedVenta) return;

        try {
            await axiosInstance.post(`/api/ventas-especiales/${selectedVenta.id}/productos`, {
                productos: [{
                    codigo: productForm.codigo,
                    descripcion: productForm.descripcion,
                    precio: parseFloat(productForm.precio),
                    unidad_manejo: productForm.unidad_manejo,
                    limite: productForm.limite ? parseInt(productForm.limite) : null
                }]
            });
            setManualModal(false);
            setProductForm({ codigo: '', descripcion: '', precio: '', unidad_manejo: 'UNIDAD', limite: '' });
            fetchProductos(selectedVenta.id);
        } catch (error) {
            alert('Error agregando producto');
        }
    };

    const toggleProductSelection = (prod) => {
        const exists = selectedProducts.find(p => p.codigo === prod.codigo);
        if (exists) {
            setSelectedProducts(selectedProducts.filter(p => p.codigo !== prod.codigo));
        } else {
            setSelectedProducts([...selectedProducts, {
                ...prod,
                precio: parseFloat(prod.precio_bulto)
            }]);
        }
    };

    const confirmImport = async () => {
        if (!selectedVenta) return;
        try {
            await axiosInstance.post(`/api/ventas-especiales/${selectedVenta.id}/productos`, {
                productos: selectedProducts
            });
            setImportModal(false);
            setSelectedProducts([]);
            fetchProductos(selectedVenta.id);
        } catch (error) {
            alert('Error importando productos');
        }
    };

    const handleViewDetails = async (venta) => {
        setSelectedVenta(venta);
        setView('details');
        setActiveTab('productos');
        fetchProductos(venta.id);
    };

    const fetchProductos = async (ventaId) => {
        try {
            const response = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/productos`);
            setSelectedVenta(prev => ({ ...prev, productos: response.data }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleUpdateProducto = async (prodId, field, value) => {
        const updatedProductos = selectedVenta.productos.map(p => {
            if (p.id === prodId) {
                return { ...p, [field]: field === 'precio' ? parseFloat(value) || 0 : field === 'observaciones' || field === 'unidadManejo' ? value : parseInt(value) || 0 };
            }
            return p;
        });
        setSelectedVenta({ ...selectedVenta, productos: updatedProductos });

        try {
            await axiosInstance.put(`/api/ventas-especiales/productos/${prodId}`, {
                [field]: field === 'precio' ? parseFloat(value) || 0 : field === 'observaciones' || field === 'unidadManejo' ? value : parseInt(value) || null
            });
        } catch (error) {
            console.error('Error actualizando producto:', error);
        }
    };

    const handleDeleteProducto = async (prodId) => {
        if (!window.confirm('¿Eliminar este producto de la venta?')) return;
        try {
            await axiosInstance.delete(`/api/ventas-especiales/productos/${prodId}`);
            setSelectedVenta({
                ...selectedVenta,
                productos: selectedVenta.productos.filter(p => p.id !== prodId)
            });
        } catch (error) {
            alert('Error eliminando producto');
        }
    };

    const handleDeleteVenta = async (ventaId) => {
        if (!window.confirm('¿Eliminar esta venta por completo? Esta acción no se puede deshacer.')) return;
        try {
            await axiosInstance.delete(`/api/ventas-especiales/${ventaId}`);
            setView('list');
            fetchVentas();
        } catch (error) {
            alert('Error eliminando venta');
        }
    };

    const handleSaveVentaMetadata = async () => {
        if (!selectedVenta) return;

        try {
            const response = await axiosInstance.put(`/api/ventas-especiales/${selectedVenta.id}`, {
                titulo: selectedVenta.titulo,
                descripcion: selectedVenta.descripcion
            });
            setSelectedVenta(prev => ({
                ...prev,
                ...response.data,
                productos: prev?.productos || response.data.productos || []
            }));
            fetchVentas();
            alert('Datos de la venta guardados');
        } catch (error) {
            alert('Error guardando la venta');
        }
    };

    const handlePublishVenta = async (ventaId) => {
        const productosSinPrecio = selectedVenta.productos?.filter(p => !p.precio || p.precio <= 0);
        if (productosSinPrecio?.length > 0) {
            alert(`No puedes publicar la venta. ${productosSinPrecio.length} producto(s) no tienen precio asignado.`);
            return;
        }

        if (!window.confirm('¿Publicar esta venta? Una vez publicada, los empleados podrán ver los productos y realizar pedidos. Los productos ya no serán editables.')) return;
        try {
            await axiosInstance.put(`/api/ventas-especiales/${ventaId}`, { activa: true });
            setSelectedVenta({ ...selectedVenta, activa: true });
            fetchVentas();
        } catch (error) {
            alert('Error publicando venta');
        }
    };

    const handleCerrarVenta = async (confirmar = false) => {
        if (!selectedVenta) return;
        let cerrarIgual = false;
        try {
            setCerrandoVenta(true);
            const res = await axiosInstance.post(`/api/ventas-especiales/${selectedVenta.id}/cerrar-venta`, { confirmar });
            setSelectedVenta(prev => ({ ...prev, ...res.data, productos: prev?.productos || [] }));
            setCerrarModal(false);
            setEditandoPedidoId(null);
            setPedidoEditItems([]);
            fetchVentas();
        } catch (error) {
            const datos = error.response?.data;
            // Pedidos sin pagar: se cierra solo si Costos lo confirma
            if (datos?.requiere_confirmacion) {
                cerrarIgual = window.confirm(`${datos.sin_pagar} pedido(s) siguen sin pagar. Al cerrar la venta, esos empleados ya no podrán subir su comprobante. ¿Cerrar igual?`);
            } else {
                alert(datos?.error || 'Error cerrando la venta');
            }
        } finally {
            setCerrandoVenta(false);
        }
        if (cerrarIgual) handleCerrarVenta(true);
    };

    const handleReabrirVenta = async () => {
        if (!selectedVenta) return;
        try {
            setReabriendoVenta(true);
            const res = await axiosInstance.post(`/api/ventas-especiales/${selectedVenta.id}/reabrir-venta`);
            setSelectedVenta(prev => ({ ...prev, ...res.data, productos: prev?.productos || [] }));
            setReabrirModal(false);
            fetchVentas();
        } catch (error) {
            alert(error.response?.data?.error || 'Error reabriendo la venta');
        } finally {
            setReabriendoVenta(false);
        }
    };

    const startEditPedido = (pedido) => {
        setEditandoPedidoId(pedido.id);
        setPedidoEditItems((pedido.items || []).map(it => ({
            productoId: it.productoId,
            descripcion: it.productoDescripcion,
            codigo: it.productoCodigo,
            precioUnitario: it.precioUnitario,
            cantidad: it.cantidad,
            peso: it.peso,
        })));
        setExpandedPedido(pedido.id);
    };

    const cancelEditPedido = () => {
        setEditandoPedidoId(null);
        setPedidoEditItems([]);
    };

    const setEditItemQty = (productoId, cantidad) => {
        setPedidoEditItems(items => items.map(it => it.productoId === productoId ? { ...it, cantidad: Math.max(0, parseInt(cantidad) || 0) } : it));
    };

    const removeEditItem = (productoId) => {
        setPedidoEditItems(items => items.filter(it => it.productoId !== productoId));
    };

    const addEditItem = (productoId) => {
        const pid = parseInt(productoId);
        if (!pid || pedidoEditItems.some(it => it.productoId === pid)) return;
        const prod = (selectedVenta.productos || []).find(p => p.id === pid);
        if (!prod) return;
        setPedidoEditItems(items => [...items, { productoId: prod.id, descripcion: prod.descripcion, codigo: prod.codigo, precioUnitario: prod.precio, cantidad: 1 }]);
    };

    const saveEditPedido = async (pedidoId) => {
        const items = pedidoEditItems.filter(it => it.cantidad > 0).map(it => ({ productoId: it.productoId, cantidad: it.cantidad, peso: it.peso }));
        if (items.length === 0) { alert('El pedido no puede quedar vacío'); return; }
        try {
            setSavingPedido(true);
            const res = await axiosInstance.put(`/api/ventas-especiales/pedidos/${pedidoId}/items`, { items });
            setPedidos(pedidos.map(p => p.id === pedidoId ? res.data.pedido : p));
            setEditandoPedidoId(null);
            setPedidoEditItems([]);
        } catch (error) {
            alert(error.response?.data?.error || 'Error guardando el pedido');
        } finally {
            setSavingPedido(false);
        }
    };

    const handleUpdatePedidoStatus = async (pedidoId, newStatus) => {
        if (!window.confirm(`¿Cambiar estado a ${newStatus}?`)) return;
        try {
            await axiosInstance.put(`/api/ventas-especiales/pedidos/${pedidoId}`, { estado: newStatus });
            setPedidos(pedidos.map(p => p.id === pedidoId ? { ...p, estado: newStatus } : p));
        } catch (error) {
            alert('Error actualizando estado');
        }
    };

    const handleSolicitarLiquidacion = async (pedido) => {
        if (!window.confirm(`${pedido.empleadoNombre} egresó sin pagar este pedido. ¿Pedir a GH que confirme el descuento en su liquidación?`)) return;
        try {
            const r = await axiosInstance.post(`/api/ventas-especiales/pedidos/${pedido.id}/liquidacion/solicitar`);
            setPedidos(prev => prev.map(p => p.id === pedido.id ? { ...p, ...r.data } : p));
        } catch (error) {
            alert(error.response?.data?.error || 'No se pudo enviar la solicitud a GH');
        }
    };

    const handleExportPedidos = () => {
        const rows = pedidos.map(p => [
            p.id,
            p.empleadoNombre,
            new Date(p.fechaPedido).toLocaleDateString(),
            p.estado,
            formatMoney(p.totalCalculado),
            p.items.map(i => `${i.peso ? i.peso + ' kg ' : i.cantidad + 'x '}${i.productoDescripcion}`).join('; ')
        ]);

        let csvContent = "\uFEFFDetail,Empleado,Fecha,Estado,Total,Items\n";
        rows.forEach(row => {
            const safeRow = row.map(cell => {
                const str = String(cell);
                return str.includes(',') || str.includes(';') ? `"${str.replace(/"/g, '""')}"` : str;
            });
            csvContent += safeRow.join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `pedidos_venta_${selectedVenta.id}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportConsolidado = async () => {
        try {
            const response = await axiosInstance.get(`/api/ventas-especiales/${selectedVenta.id}/pedidos/consolidado`);
            const rows = response.data.map(item => [
                item.codigo,
                item.producto,
                item.cantidad_total,
                formatMoney(item.total_dinero),
                item.pedidos.map(p => `${p.empleado} (${p.peso ? p.peso + ' kg' : p.cantidad})`).join('; ')
            ]);

            let csvContent = "\uFEFFCódigo,Producto,Total Unidades,Total Dinero,Detalle\n";
            rows.forEach(row => {
                const safeRow = row.map(cell => {
                    const str = String(cell);
                    return str.includes(',') || str.includes(';') ? `"${str.replace(/"/g, '""')}"` : str;
                });
                csvContent += safeRow.join(",") + "\n";
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `consolidado_venta_${selectedVenta.id}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            alert('Error exportando consolidado');
        }
    };

    const handleExportExcel = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/ventas-especiales/${selectedVenta.id}/pedidos/excel`,
                { responseType: 'blob' }
            );
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `consolidado_${selectedVenta.titulo?.replace(/\s/g, '_') || selectedVenta.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert('Error exportando Excel');
        }
    };

    // Excel con lo que dice cada captura de pago (fecha, referencia, banco, monto)
    // y si el monto cuadra con lo que el empleado debia pagar. La primera vez tarda
    // porque la IA lee las capturas; despues queda guardado y sale al instante.
    const handleExportPagos = async () => {
        setExportandoPagos(true);
        try {
            const response = await axiosInstance.get(
                `/api/ventas-especiales/${selectedVenta.id}/pagos/excel`,
                // Sin timeout: axiosInstance corta a los 60s y la PRIMERA exportacion
                // de una venta grande tarda mas (la IA lee las capturas de a 6). Al
                // cortar, el navegador mostraba error aunque el backend terminaba bien
                // y guardaba lo leido. Las siguientes ya salen al instante.
                { responseType: 'blob', timeout: 0 }
            );
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `pagos_${selectedVenta.titulo?.replace(/\s/g, '_') || selectedVenta.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (e) {
            alert('Error exportando los pagos');
        } finally {
            setExportandoPagos(false);
        }
    };

    // ===== Calendario Fases handlers =====
    const handleSaveFases = async () => {
        setSavingFases(true);
        try {
            const res = await axiosInstance.put(`/api/ventas-especiales/${selectedVenta.id}`, {
                fasesCalendario: diasPintados
            });
            setSelectedVenta(prev => ({ ...prev, fasesCalendario: res.data.fasesCalendario }));
            alert('Calendario guardado exitosamente');
        } catch (error) {
            alert('Error guardando fases');
        } finally {
            setSavingFases(false);
        }
    };

    const handleAddMonth = () => {
        if (mesesCalendario.length === 0) return;
        const last = mesesCalendario[mesesCalendario.length - 1];
        const nextMonth = (last.month + 1) % 12;
        const nextYear = last.month + 1 > 11 ? last.year + 1 : last.year;
        setMesesCalendario([...mesesCalendario, { year: nextYear, month: nextMonth }]);
        
        const newDias = { ...diasPintados };
        const daysInMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
            const dt = new Date(nextYear, nextMonth, d);
            if (dt.getDay() === 0 || dt.getDay() === 6) {
                const dateStr = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                if (!newDias[dateStr]) {
                    newDias[dateStr] = 'no_laborable';
                }
            }
        }
        setDiasPintados(newDias);
    };

    const handleRemoveLastMonth = () => {
        if (mesesCalendario.length <= 1) return;
        setMesesCalendario(mesesCalendario.slice(0, -1));
    };

    // ===== Info Pago / Archivos handlers =====
    const handleSaveInfoPago = async () => {
        setSavingInfo(true);
        try {
            const res = await axiosInstance.put(`/api/ventas-especiales/${selectedVenta.id}`, {
                infoPago: infoPagoForm
            });
            setSelectedVenta(prev => ({ ...prev, infoPago: res.data.infoPago }));
        } catch (error) {
            alert('Error guardando info de pago');
        } finally {
            setSavingInfo(false);
        }
    };

    const handleUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadingFile(true);
        try {
            const fd = new FormData();
            fd.append('archivo', file);
            fd.append('tipo', 'documento');

            const res = await axiosInstance.post(
                `/api/ventas-especiales/${selectedVenta.id}/archivos`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setSelectedVenta(prev => ({ ...prev, archivosAdjuntos: res.data.archivos }));
        } catch (error) {
            alert('Error subiendo archivo');
        } finally {
            setUploadingFile(false);
            e.target.value = '';
        }
    };

    const handleDeleteFile = async (filename) => {
        if (!window.confirm('¿Eliminar este archivo?')) return;
        try {
            const res = await axiosInstance.delete(`/api/ventas-especiales/${selectedVenta.id}/archivos/${filename}`);
            setSelectedVenta(prev => ({ ...prev, archivosAdjuntos: res.data.archivos }));
        } catch (error) {
            alert('Error eliminando archivo');
        }
    };

    // ===== DETAIL VIEW =====
    if (view === 'details' && selectedVenta) {
        // Venta cerrada: solo consulta en todas las pestanas (el backend tambien lo exige)
        const soloLectura = !!selectedVenta.cerrada;
        const isEditable = !selectedVenta.activa && !soloLectura;
        const canPublish = selectedVenta.productos?.length > 0 && 
                           Object.keys(selectedVenta.fasesCalendario || {}).length > 0 && 
                           (selectedVenta.archivosAdjuntos?.length > 0 || selectedVenta.infoPago?.banco || selectedVenta.infoPago?.pagoMovil?.banco);
                           
        return (
            <div className="max-w-7xl mx-auto px-4 pb-12">
                <button onClick={() => setView('list')} className="mb-4 text-teal-600 font-medium hover:underline">
                    &larr; Volver a lista
                </button>

                {/* Header Venta */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
                    <div className="flex justify-between items-start">
                        <div className="flex gap-6 items-start">
                            <img src="/logo_sura.png" alt="SURA" className="h-20 w-auto" />
                            <div className="flex-1">
                                {isEditable ? (
                                    <input
                                        type="text"
                                        value={selectedVenta.titulo}
                                        onChange={(e) => setSelectedVenta({ ...selectedVenta, titulo: e.target.value })}
                                        className="text-3xl font-medium text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-teal-500 outline-none bg-transparent w-full"
                                    />
                                ) : (
                                    <h1 className="text-3xl font-medium text-gray-900">{selectedVenta.titulo}</h1>
                                )}
                                {isEditable ? (
                                    <textarea
                                        value={selectedVenta.descripcion || ''}
                                        onChange={(e) => setSelectedVenta({ ...selectedVenta, descripcion: e.target.value })}
                                        className="text-lg text-gray-500 mt-2 border-b border-transparent hover:border-gray-300 focus:border-teal-500 outline-none bg-transparent w-full resize-none"
                                        rows={2}
                                    />
                                ) : (
                                    <p className="text-lg text-gray-500 mt-2">{selectedVenta.descripcion}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${soloLectura ? 'bg-slate-200 text-slate-700' : selectedVenta.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                {soloLectura ? 'CERRADA' : selectedVenta.activa ? 'PUBLICADA' : 'BORRADOR'}
                            </span>
                            {soloLectura ? null : isEditable ? (
                                <button
                                    onClick={() => handleDeleteVenta(selectedVenta.id)}
                                    className="text-xs text-red-500 hover:underline"
                                >
                                    Eliminar venta
                                </button>
                            ) : (
                                <button
                                    onClick={() => {
                                        if (window.confirm('Esta venta ya está publicada. ¿Seguro que deseas eliminarla? Se perderán todos los pedidos.')) {
                                            handleDeleteVenta(selectedVenta.id);
                                        }
                                    }}
                                    className="text-xs text-red-400 hover:underline"
                                >
                                    Eliminar venta
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex justify-between items-end mt-8 border-b border-gray-100">
                        <div className="flex gap-6">
                            {[
                                { key: 'productos', label: `Productos (${selectedVenta.productos?.length || 0})` },
                                { key: 'calendario', label: 'Calendario' },
                                { key: 'archivos', label: 'Archivos e Info' },
                                selectedVenta.activa ? { key: 'pedidos', label: 'Pedidos Recibidos' } : null,
                                selectedVenta.activa ? { key: 'cierre', label: 'Cierre' } : null,
                                { key: 'stock', label: 'Stock' },
                                { key: 'bloqueados', label: 'Bloqueados' },
                            ].filter(Boolean).map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${activeTab === tab.key ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        {isEditable && (
                            <button
                                onClick={() => handlePublishVenta(selectedVenta.id)}
                                disabled={!canPublish}
                                title={!canPublish ? 'Debes agregar productos, el calendario y métodos de pago o archivos' : 'Publicar Venta'}
                                className="flex items-center gap-2 px-4 py-2 mb-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            >
                                <CheckCircle size={16} /> Publicar Venta
                            </button>
                        )}
                        {selectedVenta.activa && !soloLectura && (
                            <button
                                onClick={() => setCerrarModal(true)}
                                title="Cerrar la venta: deja de recibir pedidos y queda solo para consulta"
                                className="flex items-center gap-2 px-4 py-2 mb-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800 transition-colors"
                            >
                                <Lock size={16} /> Cerrar venta
                            </button>
                        )}
                    </div>

                    {/* Acciones principales (Header context actions) */}
                    <div className="flex gap-2 mt-4">
                        {activeTab === 'productos' && isEditable && (
                            <>
                                <a href={withAuthToken('/api/ventas-especiales/plantilla')} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                                    <Download size={16} /> Plantilla vacía
                                </a>
                                <label className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 cursor-pointer">
                                    <Upload size={16} /> {reemplazandoPlantilla ? 'Subiendo...' : (selectedVenta.productos?.length > 0 ? 'Reemplazar plantilla' : 'Subir plantilla')}
                                    <input type="file" accept=".xlsx" className="hidden" disabled={reemplazandoPlantilla} onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f) handleReplantilla(f); }} />
                                </label>
                            </>
                        )}
                        {isEditable && (
                            <button onClick={handleSaveVentaMetadata} className="flex items-center gap-2 px-4 py-2 bg-white border border-teal-200 text-teal-700 rounded-lg text-sm hover:bg-teal-50 transition-colors">
                                <Check size={16} /> Guardar Datos
                            </button>
                        )}
                        {activeTab === 'pedidos' && (
                            <div className="flex gap-2 w-full justify-end">
                                <a href={withAuthToken(`/api/ventas-especiales/${selectedVenta.id}/pedidos/pdf`)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-sm hover:bg-blue-100 transition-colors">
                                    <FileText size={16} /> PDF de todos
                                </a>
                                <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm hover:bg-emerald-100 transition-colors">
                                    <Download size={16} /> Exportar Excel
                                </button>
                                <button
                                    onClick={handleExportPagos}
                                    disabled={exportandoPagos}
                                    title="Lee las capturas de pago con IA y verifica que el monto cuadre"
                                    className="flex items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-sm hover:bg-violet-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                    {exportandoPagos
                                        ? <><Loader2 size={16} className="animate-spin" /> Leyendo pagos...</>
                                        : <><Download size={16} /> Exportar Pagos</>}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {soloLectura && (
                    <div className="mb-6 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        <Lock size={16} className="text-slate-500 flex-shrink-0" />
                        <span className="flex-1">Venta cerrada el {formatFechaHora(selectedVenta.cerradaAt)} por {selectedVenta.cerradaPorNombre || 'Costos'}. Solo consulta.</span>
                        <button
                            type="button"
                            onClick={() => setReabrirModal(true)}
                            className="flex-shrink-0 text-xs font-medium text-slate-600 hover:text-slate-900 hover:underline"
                        >
                            Reabrir venta
                        </button>
                    </div>
                )}

                {/* ===== Contenido Tabs ===== */}

                {/* TAB PRODUCTOS */}
                {activeTab === 'productos' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Código</th>
                                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Descripción</th>
                                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Observaciones</th>
                                    <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Unidad</th>
                                    <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Precio (Bs)</th>
                                    <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Límite</th>
                                    {isEditable && <th className="w-12"></th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {selectedVenta.productos?.map(prod => (
                                    <tr key={prod.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-2 text-sm font-mono text-gray-600">{prod.codigo}</td>
                                        <td className="px-4 py-2 text-sm text-gray-900">{prod.descripcion}</td>
                                        <td className="px-4 py-2">
                                            {isEditable ? (
                                                <input
                                                    type="text"
                                                    value={prod.observaciones || ''}
                                                    placeholder="Ej: 24 unidades por caja"
                                                    onChange={(e) => handleUpdateProducto(prod.id, 'observaciones', e.target.value)}
                                                    className="w-full text-sm border border-gray-200 rounded px-2 py-1 focus:border-teal-500 outline-none"
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-500">{prod.observaciones || '-'}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            {isEditable ? (
                                                <select
                                                    value={prod.unidadManejo || 'UNIDAD'}
                                                    onChange={(e) => handleUpdateProducto(prod.id, 'unidadManejo', e.target.value)}
                                                    className="border border-gray-200 rounded px-2 py-1 text-sm focus:border-teal-500 outline-none"
                                                >
                                                    <option value="UNIDAD">Unidad</option>
                                                    <option value="BULTO">Bulto</option>
                                                    <option value="EMPAQUE">Empaque</option>
                                                    <option value="BOLSA POR KG">Bolsa por kg</option>
                                                </select>
                                            ) : (
                                                <span className="text-sm text-gray-500">{prod.unidadManejo || 'UNIDAD'}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            {isEditable ? (
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={prod.precio || ''}
                                                    placeholder="1000"
                                                    onChange={(e) => handleUpdateProducto(prod.id, 'precio', e.target.value)}
                                                    className="w-32 text-right font-bold text-teal-600 border border-gray-200 rounded px-2 py-1 focus:border-teal-500 outline-none placeholder:text-gray-300 placeholder:font-normal"
                                                />
                                            ) : (
                                                <span className="font-bold text-teal-600">Bs {formatMoney(prod.precio)}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            {isEditable ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    value={prod.limitePorEmpleado || ''}
                                                    placeholder="Ilimitado"
                                                    onChange={(e) => handleUpdateProducto(prod.id, 'limitePorEmpleado', e.target.value)}
                                                    className="w-28 text-center border border-gray-200 rounded px-2 py-1 text-sm focus:border-teal-500 outline-none placeholder:text-gray-300"
                                                />
                                            ) : (
                                                <span className="text-gray-500">
                                                    {prod.limitePorEmpleado ? `${prod.limitePorEmpleado}/emp` : 'Ilimitado'}
                                                </span>
                                            )}
                                        </td>
                                        {isEditable && (
                                            <td className="px-4 py-2 text-center">
                                                <button
                                                    onClick={() => handleDeleteProducto(prod.id)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {(!selectedVenta.productos || selectedVenta.productos.length === 0) && (
                            <div className="py-12 text-center text-gray-400">
                                No hay productos. Sube una plantilla Excel con el botón de arriba para cargarlos.
                            </div>
                        )}
                    </div>
                )}

                {/* TAB STOCK (solo lectura) */}
                {activeTab === 'stock' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        {loadingStock ? (
                            <div className="flex items-center justify-center py-16 text-gray-400">
                                <div className="w-8 h-8 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                            </div>
                        ) : stockData.length === 0 ? (
                            <div className="py-12 text-center text-gray-400">
                                No hay productos cargados en esta venta.
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Código</th>
                                        <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Descripción</th>
                                        <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Inicial</th>
                                        <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Pedido</th>
                                        <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">Restante</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {stockData.map(item => {
                                        const agotado = !item.ilimitado && item.stockRestante === 0;
                                        const pocos = !item.ilimitado && item.stockRestante > 0 && item.stockRestante < 10;
                                        return (
                                            <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-2 text-sm font-mono text-gray-600">{item.codigo}</td>
                                                <td className="px-4 py-2 text-sm text-gray-900">{item.descripcion}</td>
                                                <td className="px-4 py-2 text-sm text-right text-gray-500">{item.ilimitado ? '—' : item.stockOriginal}</td>
                                                <td className="px-4 py-2 text-sm text-right text-gray-500">{item.totalPedido}</td>
                                                <td className="px-4 py-2 text-sm text-right font-bold">
                                                    {item.ilimitado ? (
                                                        <span className="text-gray-400 font-normal">Ilimitado</span>
                                                    ) : agotado ? (
                                                        <span className="text-red-600">Agotado</span>
                                                    ) : pocos ? (
                                                        <span className="text-amber-600">{item.stockRestante}</span>
                                                    ) : (
                                                        <span className="text-gray-900">{item.stockRestante}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* TAB PEDIDOS */}
                {activeTab === 'pedidos' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-h-[400px]">
                        {loadingPedidos ? (
                            <div className="flex items-center justify-center py-16 text-gray-400">
                                <div className="w-8 h-8 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                            </div>
                        ) : pedidos.length === 0 ? (
                            <div className="text-center py-16 text-gray-500">
                                <FileText size={48} className="mx-auto text-gray-300 mb-2" />
                                <p>Aún no hay pedidos registrados</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="w-8"></th>
                                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Empleado</th>
                                        <th className="text-left text-xs font-semibold text-gray-600 uppercase px-4 py-3">Fecha</th>
                                        <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Entrega</th>
                                        <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Pago</th>
                                        <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Estado</th>
                                        <th className="text-center text-xs font-semibold text-gray-600 uppercase px-4 py-3">Comprobante</th>
                                        <th className="text-right text-xs font-semibold text-gray-600 uppercase px-4 py-3">Total (Bs)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {pedidos.map(pedido => {
                                        const isEntregado = ['entregado', 'completado'].includes(pedido.estado);
                                        const isPagado = ['pagado', 'completado'].includes(pedido.estado);
                                        const isCompletado = pedido.estado === 'completado';

                                        return (
                                            <React.Fragment key={pedido.id}>
                                                <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isCompletado ? 'bg-green-50/30' : ''}`} onClick={() => setExpandedPedido(expandedPedido === pedido.id ? null : pedido.id)}>
                                                    <td className="px-4 py-3 text-gray-400">
                                                        {expandedPedido === pedido.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{pedido.empleadoNombre}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(pedido.fechaPedido).toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={async () => {
                                                                let newStatus;
                                                                if (isEntregado) {
                                                                    newStatus = isPagado ? 'pagado' : 'pendiente';
                                                                } else {
                                                                    newStatus = isPagado ? 'completado' : 'entregado';
                                                                }
                                                                await handleUpdatePedidoStatus(pedido.id, newStatus);
                                                            }}
                                                            disabled={soloLectura}
                                                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all border disabled:cursor-not-allowed ${isEntregado ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : `bg-white border-gray-300 text-gray-300 ${soloLectura ? '' : 'hover:border-blue-400'}`}`}
                                                            title={isEntregado ? "Marcar como NO entregado" : "Marcar como ENTREGADO"}
                                                        >
                                                            <Package size={16} />
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={async () => {
                                                                let newStatus;
                                                                if (isPagado) {
                                                                    newStatus = isEntregado ? 'entregado' : 'pendiente';
                                                                } else {
                                                                    newStatus = isEntregado ? 'completado' : 'pagado';
                                                                }
                                                                await handleUpdatePedidoStatus(pedido.id, newStatus);
                                                            }}
                                                            disabled={soloLectura}
                                                            className={`w-8 h-8 rounded-full flex items-center justify-center transition-all border disabled:cursor-not-allowed ${isPagado ? 'bg-green-600 border-green-600 text-white shadow-sm' : `bg-white border-gray-300 text-gray-300 ${soloLectura ? '' : 'hover:border-green-400'}`}`}
                                                            title={isPagado ? "Marcar como NO pagado" : "Marcar como PAGADO"}
                                                        >
                                                            <Check size={16} />
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {isCompletado ? (
                                                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold border border-green-200">COMPLETADO</span>
                                                        ) : (
                                                            <span className="px-2 py-1 bg-yellow-50 text-yellow-600 rounded-full text-xs font-medium border border-yellow-200">ABIERTO</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                        {pedido.comprobantePago ? (
                                                            <a
                                                                href={withAuthToken(`/api/ventas-especiales/${selectedVenta.id}/pedido/comprobante/${pedido.comprobantePago}`)}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                                                                title="Ver comprobante de pago"
                                                            >
                                                                <Eye size={14} /> Ver
                                                            </a>
                                                        ) : pedido.cierreCategoria === 'liquidacion' ? (
                                                            <span className="px-2 py-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg" title={pedido.cierreNota || 'GH confirmó el descuento en la liquidación'}>Liquidación</span>
                                                        ) : pedido.cierreCategoria === 'liquidacion_pendiente' ? (
                                                            <span className="px-2 py-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg" title="Esperando que GH confirme el descuento">Esperando a GH</span>
                                                        ) : (!soloLectura && pedido.empleadoActivo === false && !isPagado && pedido.estado !== 'cancelado') ? (
                                                            <button
                                                                onClick={() => handleSolicitarLiquidacion(pedido)}
                                                                className="px-2 py-1 text-xs text-amber-700 bg-white border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors"
                                                                title={pedido.cierreCategoria === 'sin_conciliar' && pedido.cierreNota ? `GH indicó que no se descontó: ${pedido.cierreNota}` : 'El empleado egresó sin pagar. Pedir a GH que confirme el descuento en la liquidación.'}
                                                            >
                                                                {pedido.cierreCategoria === 'sin_conciliar' ? 'Reenviar a GH' : 'Pedir liquidación'}
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300">—</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">Bs {formatMoney(pedido.totalCalculado)}</td>
                                                </tr>
                                                {expandedPedido === pedido.id && (
                                                    <tr className="bg-gray-50/50">
                                                        <td colSpan="8" className="px-4 py-4">
                                                            <div className="pl-8 bg-white rounded border border-gray-100 p-4 w-full">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h4 className="text-xs font-bold text-gray-500 uppercase">Detalle del Pedido</h4>
                                                                    {editandoPedidoId === pedido.id ? (
                                                                        <div className="flex items-center gap-2">
                                                                            <button onClick={cancelEditPedido} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                                                                            <button onClick={() => saveEditPedido(pedido.id)} disabled={savingPedido} className="px-3 py-1.5 text-xs text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1"><Check size={14} /> {savingPedido ? 'Guardando...' : 'Guardar'}</button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center gap-2"><a href={withAuthToken(`/api/ventas-especiales/pedidos/${pedido.id}/pdf`)} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-xs text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1"><Download size={14} /> PDF piquero</a>{!soloLectura && (<button onClick={() => startEditPedido(pedido)} className="px-3 py-1.5 text-xs text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50 flex items-center gap-1"><Edit size={14} /> Editar pedido</button>)}</div>
                                                                    )}
                                                                </div>
                                                                {editandoPedidoId === pedido.id ? (
                                                                    <div>
                                                                        <table className="w-full text-sm">
                                                                            <thead>
                                                                                <tr className="text-left text-gray-400">
                                                                                    <th className="pb-2 font-normal">Producto</th>
                                                                                    <th className="pb-2 font-normal text-right">Cant.</th>
                                                                                    <th className="pb-2 font-normal text-right">Precio</th>
                                                                                    <th className="pb-2 font-normal text-right">Subtotal</th>
                                                                                    <th className="pb-2"></th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {pedidoEditItems.map((item) => (
                                                                                    <tr key={item.productoId} className="border-t border-gray-50">
                                                                                        <td className="py-2 text-gray-800">{item.descripcion} ({item.codigo}){item.peso ? ` — ${item.peso} kg` : ''}</td>
                                                                                        <td className="py-2 text-right"><input type="number" min="0" value={item.cantidad} onChange={(e) => setEditItemQty(item.productoId, e.target.value)} className="w-16 px-2 py-1 border border-gray-200 rounded text-right" /></td>
                                                                                        <td className="py-2 text-right text-gray-500">Bs {formatMoney(item.precioUnitario)}</td>
                                                                                        <td className="py-2 text-right font-medium">Bs {formatMoney((item.precioUnitario || 0) * (item.peso || item.cantidad || 0))}</td>
                                                                                        <td className="py-2 text-right"><button onClick={() => removeEditItem(item.productoId)} className="text-red-400 hover:text-red-600" title="Quitar"><Trash2 size={14} /></button></td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                        <div className="flex items-center justify-between gap-2 mt-3">
                                                                            <select onChange={(e) => { addEditItem(e.target.value); e.target.value = ''; }} defaultValue="" className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 flex-1 max-w-md">
                                                                                <option value="">+ Agregar producto...</option>
                                                                                {(selectedVenta.productos || []).filter(p => !pedidoEditItems.some(it => it.productoId === p.id)).map(p => (
                                                                                    <option key={p.id} value={p.id}>{p.descripcion} ({p.codigo}) — Bs {formatMoney(p.precio)}</option>
                                                                                ))}
                                                                            </select>
                                                                            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">Total: Bs {formatMoney(pedidoEditItems.reduce((acc, it) => acc + (it.precioUnitario || 0) * (it.peso || it.cantidad || 0), 0))}</span>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <table className="w-full text-sm">
                                                                        <thead>
                                                                            <tr className="text-left text-gray-400">
                                                                                <th className="pb-2 font-normal">Producto</th>
                                                                                <th className="pb-2 font-normal text-right">Cant.</th>
                                                                                <th className="pb-2 font-normal text-right">Precio</th>
                                                                                <th className="pb-2 font-normal text-right">Subtotal</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {pedido.items.map((item, idx) => (
                                                                                <tr key={idx} className="border-t border-gray-50">
                                                                                    <td className="py-2 text-gray-800">{item.productoDescripcion} ({item.productoCodigo})</td>
                                                                                    <td className="py-2 text-right font-medium">{item.peso ? `${item.peso} kg` : item.cantidad}</td>
                                                                                    <td className="py-2 text-right text-gray-500">Bs {formatMoney(item.precioUnitario)}</td>
                                                                                    <td className="py-2 text-right font-medium">Bs {formatMoney(item.subtotal)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* TAB CIERRE */}
                {activeTab === 'cierre' && <CierreTab ventaId={selectedVenta.id} soloLectura={soloLectura} />}

                {activeTab === 'bloqueados' && <BloqueadosTab ventaId={selectedVenta.id} soloLectura={soloLectura} />}

                {/* TAB CALENDARIO */}
                {activeTab === 'calendario' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-gray-900">Configurar Fases del Proceso</h3>
                                {isEditable && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={handleRemoveLastMonth}
                                            disabled={mesesCalendario.length <= 1}
                                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30 transition-colors"
                                        >
                                            − Mes
                                        </button>
                                        <button
                                            onClick={handleAddMonth}
                                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            + Mes
                                        </button>
                                    </div>
                                )}
                            </div>

                            <CalendarioFases
                                diasPintados={diasPintados}
                                onChangeDias={setDiasPintados}
                                editable={isEditable}
                                mesesMostrar={mesesCalendario}
                            />

                            {isEditable && (
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={handleSaveFases}
                                        disabled={savingFases}
                                        className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                                    >
                                        {savingFases ? 'Guardando...' : 'Guardar Calendario'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* TAB ARCHIVOS E INFO */}
                {activeTab === 'archivos' && (
                    <div className="space-y-6">
                        {/* Archivos adjuntos */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Archivos Adjuntos (PDFs)</h3>

                            {/* Lista de archivos existentes */}
                            {(selectedVenta.archivosAdjuntos || []).length > 0 && (
                                <div className="space-y-2 mb-4">
                                    {selectedVenta.archivosAdjuntos.map((archivo, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                                                    <FileText size={18} className="text-red-500" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{archivo.originalName}</p>
                                                    <p className="text-xs text-gray-400 capitalize">{archivo.tipo}</p>
                                                </div>
                                            </div>
                                            {!soloLectura && (
                                                <button
                                                    onClick={() => handleDeleteFile(archivo.filename)}
                                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Upload */}
                            {soloLectura ? (
                                (selectedVenta.archivosAdjuntos || []).length === 0 && <p className="text-sm text-gray-400">No hay archivos adjuntos.</p>
                            ) : (
                                <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm cursor-pointer hover:bg-gray-200 transition-colors w-fit">
                                    <Upload size={16} />
                                    {uploadingFile ? 'Subiendo...' : 'Subir PDF'}
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        className="hidden"
                                        onChange={handleUploadFile}
                                        disabled={uploadingFile}
                                    />
                                </label>
                            )}
                        </div>

                        {/* Info de Pago */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Información de Pago</h3>

                            {/* fieldset disabled: con la venta cerrada ningun campo se puede editar */}
                            <fieldset disabled={soloLectura} className="min-w-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Transferencia */}
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Transferencia Bancaria</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Banco</label>
                                            <input type="text" value={infoPagoForm.banco} onChange={e => setInfoPagoForm({ ...infoPagoForm, banco: e.target.value })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="Ej: Banesco" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Cuenta</label>
                                            <input type="text" value={infoPagoForm.cuenta} onChange={e => setInfoPagoForm({ ...infoPagoForm, cuenta: e.target.value })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="0134-XXXX-XX-XXXXXXXXXX" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">RIF</label>
                                            <input type="text" value={infoPagoForm.rif} onChange={e => setInfoPagoForm({ ...infoPagoForm, rif: e.target.value })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="J-XXXXXXXX-X" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Titular</label>
                                            <input type="text" value={infoPagoForm.titular} onChange={e => setInfoPagoForm({ ...infoPagoForm, titular: e.target.value })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="SURA de Venezuela C.A." />
                                        </div>
                                    </div>
                                </div>

                                {/* Pago Móvil */}
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Pago Móvil</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Banco</label>
                                            <input type="text" value={infoPagoForm.pagoMovil.banco} onChange={e => setInfoPagoForm({ ...infoPagoForm, pagoMovil: { ...infoPagoForm.pagoMovil, banco: e.target.value } })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="Ej: Banesco" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Teléfono</label>
                                            <input type="text" value={infoPagoForm.pagoMovil.telefono} onChange={e => setInfoPagoForm({ ...infoPagoForm, pagoMovil: { ...infoPagoForm.pagoMovil, telefono: e.target.value } })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="0412-XXXXXXX" />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">RIF / Cédula</label>
                                            <input type="text" value={infoPagoForm.pagoMovil.rif} onChange={e => setInfoPagoForm({ ...infoPagoForm, pagoMovil: { ...infoPagoForm.pagoMovil, rif: e.target.value } })} className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none" placeholder="J-XXXXXXXX-X" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Emails */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Emails para pedidos (uno por línea)</label>
                                    <textarea
                                        value={infoPagoForm.emailsPedido}
                                        onChange={e => setInfoPagoForm({ ...infoPagoForm, emailsPedido: e.target.value })}
                                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none resize-none"
                                        rows={3}
                                        placeholder="pedidos@sura.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Emails para pagos (uno por línea)</label>
                                    <textarea
                                        value={infoPagoForm.emailsPago}
                                        onChange={e => setInfoPagoForm({ ...infoPagoForm, emailsPago: e.target.value })}
                                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 outline-none resize-none"
                                        rows={3}
                                        placeholder="pagos@sura.com"
                                    />
                                </div>
                            </div>
                            </fieldset>

                            {!soloLectura && (
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={handleSaveInfoPago}
                                        disabled={savingInfo}
                                        className="px-6 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                                    >
                                        {savingInfo ? 'Guardando...' : 'Guardar Info de Pago'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Modal Cerrar venta */}
                <Modal isOpen={cerrarModal} onClose={() => { if (!cerrandoVenta) setCerrarModal(false); }}>
                    <Modal.Header>Cerrar venta</Modal.Header>
                    <Modal.Body className="space-y-3">
                        <p className="text-sm text-gray-700">Vas a cerrar <span className="font-semibold">{selectedVenta.titulo}</span>.</p>
                        <p className="text-sm text-amber-800 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                            Al cerrar la venta ya no se podrá editar nada (productos, pedidos, stock, archivos ni cierre de facturas) ni eliminarla. Quedará solo para consulta.
                        </p>
                        <p className="text-xs text-gray-500">Los empleados dejarán de verla en la tienda. Los PDF y Excel se podrán seguir descargando. Si se cierra por error, se puede reabrir.</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" onClick={() => setCerrarModal(false)} disabled={cerrandoVenta} className="px-4 py-2 text-gray-600">Cancelar</button>
                        <button
                            type="button"
                            onClick={() => handleCerrarVenta()}
                            disabled={cerrandoVenta}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                        >
                            {cerrandoVenta ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Cerrar venta
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Reabrir venta */}
                <Modal isOpen={reabrirModal} onClose={() => { if (!reabriendoVenta) setReabrirModal(false); }}>
                    <Modal.Header>Reabrir venta</Modal.Header>
                    <Modal.Body className="space-y-3">
                        <p className="text-sm text-gray-700">Vas a reabrir <span className="font-semibold">{selectedVenta.titulo}</span>.</p>
                        <p className="text-sm text-amber-800 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                            Volverá a estar publicada: se podrán editar de nuevo los pedidos, el stock, los archivos y el cierre de facturas, y los empleados la verán otra vez en la tienda mientras siga dentro de sus fechas.
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" onClick={() => setReabrirModal(false)} disabled={reabriendoVenta} className="px-4 py-2 text-gray-600">Cancelar</button>
                        <button
                            type="button"
                            onClick={handleReabrirVenta}
                            disabled={reabriendoVenta}
                            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                        >
                            {reabriendoVenta ? <Loader2 size={16} className="animate-spin" /> : <Unlock size={16} />} Reabrir venta
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Importación */}
                <Modal isOpen={importModal} onClose={() => setImportModal(false)} size="2xl">
                    <Modal.Header>Seleccionar Productos para Importar</Modal.Header>
                    <Modal.Body className="max-h-[70vh] overflow-y-auto p-4">
                        {importing ? (
                            <div className="text-center py-8">Cargando productos del servidor...</div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex justify-end mb-2">
                                    <span className="text-sm text-gray-500">{selectedProducts.length} seleccionados</span>
                                </div>
                                {externalProducts.map((prod, idx) => {
                                    const isSelected = selectedProducts.some(p => p.codigo === prod.codigo);
                                    return (
                                        <div
                                            key={idx}
                                            onClick={() => toggleProductSelection(prod)}
                                            className={`p-3 rounded-lg border cursor-pointer flex justify-between items-center hover:bg-gray-50 ${isSelected ? 'bg-teal-50 border-teal-500 ring-1 ring-teal-500' : 'border-gray-200'}`}
                                        >
                                            <div>
                                                <p className="font-medium text-gray-900">{prod.descripcion}</p>
                                                <p className="text-sm text-gray-500">{prod.codigo} - {prod.unidades} u/caja</p>
                                            </div>
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-teal-600 border-teal-600 text-white' : 'border-gray-300'}`}>
                                                {isSelected && <span className="text-xs">✓</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <button onClick={() => setImportModal(false)} className="px-4 py-2 text-gray-600">Cancelar</button>
                        <button
                            onClick={confirmImport}
                            disabled={selectedProducts.length === 0}
                            className="px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50"
                        >
                            Importar {selectedProducts.length} Productos
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* Modal Agregar Producto Manual */}
                <Modal isOpen={manualModal} onClose={() => setManualModal(false)}>
                    <Modal.Header>Agregar Producto</Modal.Header>
                    <form onSubmit={handleAddManualProduct}>
                        <Modal.Body className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
                                    <input
                                        required
                                        type="text"
                                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                        value={productForm.codigo}
                                        onChange={e => setProductForm({ ...productForm, codigo: e.target.value })}
                                        placeholder="Ej: PROD001"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Precio (Bs)</label>
                                    <input
                                        required
                                        type="number"
                                        step="0.01"
                                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                        value={productForm.precio}
                                        onChange={e => setProductForm({ ...productForm, precio: e.target.value })}
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                                <input
                                    required
                                    type="text"
                                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                    value={productForm.descripcion}
                                    onChange={e => setProductForm({ ...productForm, descripcion: e.target.value })}
                                    placeholder="Nombre del producto"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unidad</label>
                                    <select
                                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                        value={productForm.unidad_manejo}
                                        onChange={e => setProductForm({ ...productForm, unidad_manejo: e.target.value })}
                                    >
                                        <option value="UNIDAD">Unidad</option>
                                        <option value="EMPAQUE">Empaque</option>
                                        <option value="BULTO">Bulto</option>
                                        <option value="CAJA">Caja</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Límite por empleado</label>
                                    <input
                                        type="number"
                                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                        value={productForm.limite}
                                        onChange={e => setProductForm({ ...productForm, limite: e.target.value })}
                                        placeholder="Sin límite"
                                    />
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" onClick={() => setManualModal(false)} className="px-4 py-2 text-gray-600">Cancelar</button>
                            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-lg">Agregar</button>
                        </Modal.Footer>
                    </form>
                </Modal>
            </div>
        );
    }

    // COMPARATIVA VIEW
    if (view === 'comparativa') {
        return (
            <div className="max-w-7xl mx-auto px-4 pb-12">
                <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="p-5 border-b border-gray-100">
                        <button onClick={() => setView('list')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-3 text-sm">
                            <ChevronDown size={16} className="rotate-90" /> Volver
                        </button>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><BarChart3 size={22} /> Comparativa entre Ventas</h2>
                    </div>
                    {comparativaLoading ? (
                        <div className="text-center py-16 text-gray-400">Cargando...</div>
                    ) : (
                        <div className="p-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                {comparativa.map(v => (
                                    <div key={v.id} className={`rounded-2xl border p-5 ${v.cerrada ? 'border-slate-200 bg-slate-50/40' : v.activa ? 'border-green-200 bg-green-50/30' : 'border-gray-200'}`}>
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-bold text-gray-900 text-sm">{v.titulo}</h3>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${v.cerrada ? 'bg-slate-200 text-slate-600' : v.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {v.cerrada ? 'Cerrada' : v.activa ? 'Publicada' : 'Borrador'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                            <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
                                                <p className="text-2xl font-bold text-teal-600">{v.totalPedidos}</p>
                                                <p className="text-[10px] text-gray-400 uppercase">Pedidos</p>
                                            </div>
                                            <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
                                                <p className="text-lg font-bold text-gray-900">Bs {formatMoney(v.totalMonto)}</p>
                                                <p className="text-[10px] text-gray-400 uppercase">Total</p>
                                            </div>
                                            <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
                                                <p className="text-lg font-bold text-blue-600">{v.pagados}</p>
                                                <p className="text-[10px] text-gray-400 uppercase">Pagados</p>
                                            </div>
                                            <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
                                                <p className="text-lg font-bold text-emerald-600">{v.entregados}</p>
                                                <p className="text-[10px] text-gray-400 uppercase">Entregados</p>
                                            </div>
                                        </div>
                                        {v.topProductos?.length > 0 && (
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5 flex items-center gap-1"><TrendingUp size={10} /> Top productos</p>
                                                {v.topProductos.map((tp, i) => (
                                                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                                                        <span className="text-gray-300 font-bold">{i + 1}.</span>
                                                        <span className="flex-1 text-gray-700 truncate">{tp.nombre}</span>
                                                        <span className="text-gray-400">{tp.cantidad} und.</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // LIST VIEW
    // Dos mini pestanas: abiertas (publicadas y borradores) y cerradas
    const ventasCerradas = ventas.filter(v => v.cerrada);
    const ventasPublicadas = ventas.filter(v => !v.cerrada);
    const ventasLista = listaTab === 'cerradas' ? ventasCerradas : ventasPublicadas;

    return (
        <div className="max-w-7xl mx-auto px-4 pb-12">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6">
                <div className="p-5 border-b border-gray-100 flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Ventas Especiales</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Gestión de ventas trimestrales para empleados</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={async () => {
                                setView('comparativa');
                                setComparativaLoading(true);
                                try {
                                    const r = await axiosInstance.get('/api/ventas-especiales/comparativa');
                                    setComparativa(r.data.ventas || []);
                                } catch (e) { console.error(e); }
                                finally { setComparativaLoading(false); }
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-xl text-sm hover:bg-indigo-100 transition-colors"
                        >
                            <BarChart3 size={16} /> Comparativa
                        </button>
                        <button
                            onClick={() => setCreateModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl font-medium text-sm hover:bg-teal-700 transition-colors"
                        >
                            <Plus size={16} /> Nueva Venta
                        </button>
                    </div>
                </div>

                <div className="px-5 pt-3 flex gap-6 border-b border-gray-100">
                    {[
                        { key: 'publicadas', label: 'Ventas Publicadas', n: ventasPublicadas.length },
                        { key: 'cerradas', label: 'Ventas Cerradas', n: ventasCerradas.length },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setListaTab(tab.key)}
                            className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${listaTab === tab.key ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            {tab.label}
                            <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs ${listaTab === tab.key ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>{tab.n}</span>
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16 text-gray-400">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                    </div>
                ) : ventasLista.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                        <Package size={48} className="mb-4 text-gray-300" />
                        <h3 className="text-lg font-semibold text-gray-600">
                            {ventas.length === 0 ? 'No hay ventas creadas' : listaTab === 'cerradas' ? 'No hay ventas cerradas' : 'No hay ventas publicadas ni en borrador'}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {listaTab === 'cerradas' ? 'Cuando cierres una venta publicada aparecerá aquí' : 'Crea una nueva venta para comenzar'}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {ventasLista.map(venta => (
                            <div
                                key={venta.id}
                                className="p-5 hover:bg-gray-50/50 transition-colors cursor-pointer group"
                                onClick={() => handleViewDetails(venta)}
                            >
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${venta.cerrada ? 'bg-slate-100' : 'bg-teal-50'}`}>
                                            {venta.cerrada ? <Lock className="text-slate-500" size={20} /> : <Package className="text-teal-600" size={22} />}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-semibold text-gray-900">{venta.titulo}</h3>
                                                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${venta.cerrada ? 'bg-slate-200 text-slate-600' : venta.activa ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {venta.cerrada ? 'Cerrada' : venta.activa ? 'Publicada' : 'Borrador'}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500 mt-0.5">{venta.descripcion || 'Sin descripción'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-gray-900">{venta.totalProductos || 0}</p>
                                            <p className="text-xs text-gray-400">productos</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-bold text-gray-900">{venta.totalPedidos || 0}</p>
                                            <p className="text-xs text-gray-400">pedidos</p>
                                        </div>
                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 group-hover:bg-teal-100 group-hover:text-teal-600 transition-colors">
                                            <ChevronDown size={16} className="-rotate-90" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal isOpen={createModal} onClose={() => setCreateModal(false)}>
                <Modal.Header>Crear Nueva Venta Especial</Modal.Header>
                <form onSubmit={handleCreateVenta}>
                    <Modal.Body className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                            <input
                                required
                                type="text"
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                value={formData.titulo}
                                onChange={e => setFormData({ ...formData, titulo: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                            <textarea
                                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                                value={formData.descripcion}
                                onChange={e => setFormData({ ...formData, descripcion: e.target.value })}
                            />
                        </div>
                        <div className="border-t border-gray-100 pt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Productos (plantilla Excel) — obligatoria</label>
                            <div className="flex flex-wrap items-center gap-3">
                                <a href={withAuthToken('/api/ventas-especiales/plantilla')} className="flex items-center gap-2 px-3 py-2 text-sm text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-50">
                                    <Download size={16} /> Descargar plantilla vacía
                                </a>
                                <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                                    <Upload size={16} /> {plantillaFile ? plantillaFile.name : 'Subir plantilla lista'}
                                    <input type="file" accept=".xlsx" className="hidden" onChange={e => setPlantillaFile(e.target.files[0] || null)} />
                                </label>
                            </div>
                            <p className="text-xs text-gray-400 mt-2">Descarga la plantilla vacía, llénala y súbela. La venta se crea con todos sus productos.</p>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="button" onClick={() => { setCreateModal(false); setPlantillaFile(null); }} className="px-4 py-2 text-gray-600">Cancelar</button>
                        <button type="submit" disabled={importandoPlantilla || !plantillaFile} className="px-4 py-2 bg-teal-600 text-white rounded-lg disabled:opacity-50">{importandoPlantilla ? 'Importando...' : 'Crear'}</button>
                    </Modal.Footer>
                </form>
            </Modal>
        </div>
    );
};

export default VentaEspecialAdmin;
